<script setup lang="ts">
  import { useUser } from '@gat-ship/user'
  import type { SupportSession } from '@gat-ship/types'
  import { IconDotsVertical } from '@gat-ship/icons'
  import { onMounted, ref, useTemplateRef } from 'vue'
  import { useSupportApiCalls } from '../../composables/useSupportApiCalls'
  import { useSupportSessionCountdown } from '../../composables/useSupportSessionCountdown'
  import { useSupportSessionPermissions } from '../../composables/useSupportSessionPermissions'
  import SupportSessionModalEndCurrent from '../modals/SupportSessionModalEndCurrent.vue'
  import SupportSessionModalDetails from '../modals/SupportSessionModalDetails.vue'
  import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem
  } from '@gat-ship/vue-components'
  import { Button } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'

  const user = useUser()
  const { getSupportSessionRequest } = useSupportApiCalls()
  const { canEndSupportSession } = useSupportSessionPermissions()
  const supportSession = ref<SupportSession>()

  const { isExpired, label, warningLevel } = useSupportSessionCountdown(
    () => supportSession.value?.grantedTo
  )

  onMounted(async () => {
    if (user.supportSessionId.value) {
      const result = await getSupportSessionRequest(user.supportSessionId.value)
      supportSession.value = result.session
    }
  })

  // The header is self-contained: it renders only inside a support
  // session, hosts its own end/details modals, and does not depend on
  // the SupportModalProvider (which mounts on the support surfaces, not
  // at the app root).
  const modalEndSupportSessionCurrentRef = useTemplateRef(
    'modalEndSupportSessionCurrentRef'
  )
  const modalSupportSessionDetailsRef = useTemplateRef(
    'modalSupportSessionDetailsRef'
  )

  /** Ending from the header always ends the session this tab is inside. */
  function endCurrentSupportSession() {
    if (!supportSession.value) return
    modalEndSupportSessionCurrentRef.value?.showModal({
      supportSession: supportSession.value
    })
  }

  function showSupportSessionDetails() {
    if (!supportSession.value) return
    modalSupportSessionDetailsRef.value?.showModal({
      supportSession: supportSession.value
    })
  }
</script>
<template>
  <header
    v-if="user.isSupportSession.value"
    class="absolute left-0 right-0 top-0 flex shrink-0 items-center justify-between gap-2 p-4 text-xs dark:text-black"
    :class="{
      'bg-support-session': warningLevel !== 'critical',
      'bg-red-100': warningLevel === 'critical'
    }"
    :style="{
      height: 'var(--app-status-bar-height)'
    }"
  >
    <div class="flex items-center gap-2">
      <span>{{ supportSession?.ownerTenantName }}</span>
    </div>

    <span>
      <span> You are acting on behalf of </span>
      <span class="font-medium">{{ user.userProfile.value?.fullName }}</span>
    </span>
    <div
      class="flex items-center gap-2"
      :class="{
        'text-destructive animate-pulse': warningLevel === 'critical'
      }"
    >
      <span v-if="warningLevel === 'warning'" class="w-[150px] text-right"
        >expires in {{ label }}</span
      >
      <DropdownMenu v-if="supportSession">
        <DropdownMenuTrigger as-child>
          <Button size="icon" variant="ghost" class="h-6 dark:text-black">
            <IconDotsVertical class="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            v-if="canEndSupportSession(supportSession)"
            @click="endCurrentSupportSession()"
          >
            End Support Session
          </DropdownMenuItem>
          <DropdownMenuItem @click="showSupportSessionDetails()">
            More Details
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>
  <AlertDialog :open="isExpired">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>The support session has expired!</AlertDialogTitle>
        <AlertDialogDescription>You can close this tab.</AlertDialogDescription>
      </AlertDialogHeader>
    </AlertDialogContent>
  </AlertDialog>
  <SupportSessionModalEndCurrent ref="modalEndSupportSessionCurrentRef" />
  <SupportSessionModalDetails ref="modalSupportSessionDetailsRef" />
</template>
